<template>
  <div class="dot-plot" v-if="numbers">
    <div class="region-container" v-for="{key, text, value: rows} in numbers" :key="key" :class="key">
      <h4>{{text}}</h4>
      <div class="country-container" v-for="row in rows" :key="row.key" :class="key">
        <p>{{row.country}}</p>
        <p v-html="summarize(row)"></p>
        <div class="cases-container" :style="{height: getHeight(row)}">
          <span class="case" v-for="i in row.cases" :key="i" :class="getColor(i, row)"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {store} from './wuhanData'

export default {
  computed: {
    numbers () {
      if (!store.rawData) return null
      const excluded = new Set(this.$store.state.customs.timeseries.items.map(row => row.country))
      const regions = ['Asia', 'Central America and the Caribbean', 'Europe', 'North America', 'Northern Africa & Middle East', 'Oceania', 'South America', 'Sub-saharan Africa']
      return regions.map(region => {
        const filtered = store.rawData.filter(row => row.region === region && !excluded.has(row.country))
        filtered.forEach(row => {
          row.key = row.country.toLowerCase().replace(/[^a-z]/g, '-')
        })
        return {
          key: region.toLowerCase().replace(/[^a-z]/g, '-'),
          text: region,
          value: filtered
        }
      })
    }
  },
  filters: {
    formatNumber (n) {
      return n.toLocaleString()
    }
  },
  methods: {
    summarize (row) {
      let summarized = `<span class="cases bold">${row.cases.toLocaleString()}</span> case${row.cases > 1 ? 's' : ''}`
      if (row.recovered > 0) summarized += ` - <span class="grey"><span class="recovered bold">${row.recovered.toLocaleString()}</span> recovered</span>`
      if (row.deaths > 0) summarized += `<span class="grey">, <span class="deaths bold">${row.deaths.toLocaleString()}</span> death${row.deaths > 1 ? 's' : ''}</span>`
      return summarized
    },
    getColor (i, row) {
      i = row.cases - i
      if (i >= row.deaths + row.recovered) return 'active'
      if (i >= row.deaths) return 'recovered'
      return 'deaths'
    },
    getHeight (row) {
      if (this.$window.innerWidth < 768) {
        // MOBILE
        if (row.cases > 400) return Math.ceil(row.cases / 75) * 5 + 'px'
        if (row.cases > 50) return 10 * 5 + 'px' // Up to 400, 40 per row
        if (row.cases > 20) return 5 * 5 + 'px' // Up to 50, 10 per row
        if (row.cases > 2) return 2 * 5 + 'px' // Up to 20, 10 per row
        return '5px'
      } else {
        // DESKTOP
        if (row.cases > 800) return Math.ceil(row.cases / 80) * 8 + 'px'
        if (row.cases > 320) return 10 * 8 + 'px' // Up to 800, 80 per row
        if (row.cases > 240) return 4 * 8 + 'px' // Up to 320, 80 per row
        if (row.cases > 40) return 3 * 8 + 'px' // Up to 240, 80 per row
        if (row.cases > 2) return 2 * 8 + 'px' // Up to 40, 20 per row
        return '8px'
      }
    }
  }
}
</script>

<style lang="scss">
.dot-plot {
  .region-container {
    border-top: 1px solid black;
    margin-bottom: 40px;
    position: relative;

    h4 {
      padding: 10px 0;
      margin-bottom: 10px;
      background-color: rgba(255, 255, 255, 0.95);
      position: sticky;
      top: 0;
    }
  }

  .country-container {
    margin-bottom: 20px;

    p {
      font-family: $font-CuratorRegular;
      font-size: 15px;
      line-height: 18px;
    }

    p:first-child {
      text-transform: uppercase;
    }

    span.bold {
      font-family: $font-CuratorBold;
    }

    span.grey {
      color: #999999;
    }

    span.recovered {
      color: rgb(2, 199, 191);
    }

    span.deaths {
      color: rgb(149, 35, 112);
    }
  }

  .cases-container {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: flex-start;
    width: 100%;
    margin-top: 10px;
  }

  .case {
    display: block;
    width: 4px;
    height: 4px;
    border-radius: 2px;
    margin: 0 0.5px 0.5px 0;

    @include breakpoint($d_medium) {
      width: 7px;
      height: 7px;
      border-radius: 3.5px;
      margin: 0 1px 1px 0;
    }

    &.active {
      background-color: rgb(212, 136, 61);
    }

    &.recovered {
      background-color: rgb(2, 199, 191);
    }

    &.deaths {
      background-color: rgb(149, 35, 112);
    }
  }
}
</style>
